<template>
  <EdroneDrawer :open="isOpen" width="35rem" @close="$emit('close')">
    <div class="tw:flex tw:flex-col tw:h-full">
      <EdroneNavigationTop :show-back-icon="showBack" type="drawer" @close="$emit('close')">
        <template #header>
          <EdroneHeader :title="translations.drawer.title" type="h2" />
        </template>
        <template #buttons>
          <a :href="snapshot.context.helpUrl" target="_blank" rel="noopener noreferrer">
            <EdroneButton type="link-outline">
              {{ trans._packageManager.more }}
              <template #right-icon>
                <FontAwesomeIcon icon="fa-light fa-arrow-up-right-from-square" />
              </template>
            </EdroneButton>
          </a>
        </template>
      </EdroneNavigationTop>

      <div class="tw:flex-1 tw:flex tw:flex-col tw:overflow-y-scroll tw:pt-2">
        <div v-if="snapshot.hasTag('loading')" class="tw:h-full tw:flex tw:justify-center tw:items-center tw:px-6">
          <EdroneResultState type="loading" />
        </div>
        <div v-else class="tw:flex tw:flex-1 tw:flex-col tw:gap-4">
          <div
            class="tw:mx-6 tw:border tw:rounded-sm tw:border-default-200 tw:p-3 tw:flex tw:flex-col tw:gap-3 tw:bg-default-000"
          >
            <p v-for="item in translations.drawer.benefits" :key="item" class="tw:body-2--bold tw:flex tw:gap-2">
              <FontAwesomeIcon class="tw:text-xl tw:text-green-500" icon="fa-solid fa-badge-check" />
              {{ item }}
            </p>
          </div>
          <div class="tw:mx-6 tw:flex tw:flex-col tw:gap-2">
            <EdroneRadioButton
              v-if="snapshot.context.data.recommendedPackage"
              :checked="selected === 0"
              :value="0"
              orientation="horizontal"
              unique-id=""
              @change="handleSelectRecommended"
            >
              <template #custom>
                <div class="tw:p-1 tw:flex tw:flex-col tw:gap-1">
                  <p class="tw:heading-2">
                    {{
                      `${formatNumberV2(snapshot.context.data.recommendedPackageSize)} ${
                        translations.drawer.options.text
                      }`
                    }}
                  </p>
                  <div class="tw:flex tw:items-center tw:gap-1">
                    <p class="tw:body-1--bold">
                      {{
                        formatNumberV2(
                          snapshot.context.data.recommendedPackage.price * snapshot.context.data.recommendedPackageSize,
                          {
                            style: "currency",
                            currency,
                          }
                        )
                      }}
                    </p>
                    <span v-if="!isBrazil" class="tw:body-1 tw:text-default-700 tw:font-normal">
                      + {{ translations.drawer.options.vat.toUpperCase() }} /
                      {{ translations.drawer.options.month }}
                    </span>
                    <EdroneBadge
                      v-if="recommendedDiscount"
                      :label="`-${recommendedDiscount}%`"
                      class="tw:ml-1"
                      solid
                      size="small"
                    />
                  </div>
                  <p class="tw:body-2 tw:text-default-700">
                    {{
                      formatNumberV2(snapshot.context.data.recommendedPackage.price, { style: "currency", currency })
                    }}
                    /
                    {{ translations.drawer.options.text }}
                  </p>
                </div>
              </template>
              <template #badge>
                <EdroneBadge :label="translations.drawer.options.recommended" solid type="info" size="small" />
              </template>
            </EdroneRadioButton>
            <EdroneRadioButton
              :checked="selected === 1"
              :value="1"
              orientation="horizontal"
              unique-id=""
              @change="handleSelectCustom"
            >
              <template #custom>
                <div class="tw:p-1 tw:flex tw:flex-col tw:gap-1">
                  <p class="tw:heading-2">
                    {{ translations.drawer.options.custom.title }}
                  </p>
                  <p class="tw:body-2 tw:text-default-700">
                    {{ translations.drawer.options.custom.description }}
                  </p>
                  <div
                    :class="[
                      selected === 1 ? 'tw:h-[156px] tw:opacity-100' : 'tw:h-0 tw:opacity-0 tw:overflow-hidden',
                      'tw:transition-all',
                    ]"
                  >
                    <p class="tw:body-1 tw:mb-1 tw:mt-2">
                      {{ translations.drawer.options.custom.amount }}
                    </p>
                    <div class="tw:flex tw:gap-4 tw:mb-3">
                      <EdroneSliderInput
                        :max="100001"
                        :min="0"
                        :step="100"
                        :value="snapshot.context.data.customPackageSize"
                        :with-thumb="false"
                        @update-value="handlePackageSizeChange"
                      />
                      <EdroneNumberInput
                        :max="100001"
                        :min="1"
                        :step="10"
                        :value="snapshot.context.data.customPackageSize"
                        class="tw:w-[9.375rem]"
                        unique-id="PackageManager--number-input"
                        @update-value="handlePackageSizeChange"
                      />
                    </div>
                    <div
                      v-if="snapshot.context.data.selectedPackage"
                      class="tw:grid tw:grid-cols-2 tw:gap-1 tw:p-3 tw:bg-blue-100 tw:rounded-sm"
                    >
                      <p class="tw:body-2">
                        {{ translations.drawer.options.custom.total }}
                      </p>
                      <p class="tw:body-2--bold tw:text-right">
                        {{ formatTotalSelectedPackagePrice }}
                        <span v-if="!isBrazil" class="tw:font-normal tw:text-default-700">
                          + {{ translations.drawer.options.vat.toUpperCase() }}
                        </span>
                      </p>
                      <p class="tw:body-2">
                        {{ translations.drawer.options.custom.price }}
                      </p>
                      <p class="tw:body-2--bold tw:text-right">
                        {{
                          formatNumberV2(snapshot.context.data.selectedPackage.price, {
                            style: "currency",
                            currency: currency,
                          })
                        }}
                      </p>
                    </div>
                  </div>
                </div>
              </template>
            </EdroneRadioButton>

            <EdroneButton class="tw:w-fit" size="small" type="text" @click="send({ type: 'SHOW_PRICING' })">
              {{ translations.drawer.buttons.pricing }}
            </EdroneButton>
          </div>
          <div class="tw:mt-auto tw:sticky tw:bottom-0">
            <div
              v-if="!isBrazil && showCoupon"
              class="tw:bg-white tw:flex tw:gap-2 tw:justify-end tw:border-t tw:border-t-default-200 tw:py-4 tw:px-6 tw:w-full"
            >
              <EdroneAlert
                v-if="snapshot.context.data.coupon && snapshot.context.data.coupon.active"
                :title="getCouponInfo()"
                class="tw:w-full"
                :is-vertical="false"
                show-close-icon
                type="success"
                @close="showCoupon = false"
              >
                <template #custom-icon>
                  <FontAwesomeIcon class="tw:text-green-500" icon="fa-light fa-circle-check" />
                </template>
              </EdroneAlert>
              <template v-else>
                <EdroneInput
                  :disabled="isValidatingCoupon"
                  :error-msg="invalidCoupon ? translations.drawer.coupon.invalid : ''"
                  :invalid="!!invalidCoupon"
                  :placeholder="translations.drawer.coupon.placeholder"
                  :value="snapshot.context.couponCode"
                  class="tw:!w-1/3"
                  @enter="send({ type: 'APPLY_COUPON', coupon: $event.toString() })"
                  @input="send({ type: 'UPDATE_COUPON_CODE', couponCode: $event })"
                />
                <EdroneButton
                  :disabled="isValidatingCoupon"
                  :loading="isValidatingCoupon"
                  type="outline"
                  @click="send({ type: 'APPLY_COUPON', coupon: snapshot.context.couponCode })"
                >
                  {{ translations.drawer.coupon.apply }}
                </EdroneButton>
              </template>
            </div>
            <div
              v-if="snapshot.context.data.selectedPackage"
              class="tw:bg-default-000 tw:border-t tw:border-t-default-200 tw:py-4 tw:px-6 tw:flex tw:flex-col tw:gap-2"
            >
              <div class="tw:flex tw:gap-2 tw:justify-between">
                <p class="tw:body-2--bold">
                  {{ translations.drawer.summary.selected }} ({{ snapshot.context.data.customPackageSize }}
                  {{ translations.drawer.options.text }})
                </p>
                <p class="tw:body-2--bold">
                  {{ formatTotalSelectedPackagePrice }}
                </p>
              </div>
              <hr />
              <div
                v-if="
                  snapshot.context.data.coupon &&
                  snapshot.context.data.coupon.active &&
                  snapshot.context.data.coupon.duration === 'ONCE'
                "
                class="tw:flex tw:gap-2 tw:justify-between"
              >
                <p class="tw:heading-3">{{ translations.drawer.summary.first }}</p>
                <p class="tw:heading-3 tw:flex tw:gap-2 tw:items-center">
                  <span class="tw:line-through">
                    {{ formatTotalSelectedPackagePrice }}
                  </span>
                  {{
                    formatNumberV2(totalSelectedPackagePrice - getCouponDiscount(totalSelectedPackagePrice), {
                      style: "currency",
                      currency,
                    })
                  }}

                  <EdroneBadge
                    v-if="!isBrazil"
                    :label="`+ ${translations.drawer.options.vat.toUpperCase()}`"
                    class="tw:font-normal"
                    size="small"
                  />
                </p>
              </div>
              <div class="tw:flex tw:gap-2 tw:justify-between">
                <p class="tw:heading-3">{{ translations.drawer.summary.monthly }}</p>
                <p class="tw:heading-3 tw:flex tw:gap-2 tw:items-center">
                  <span
                    v-if="snapshot.context.data.coupon?.active && snapshot.context.data.coupon.duration === 'FOREVER'"
                    class="tw:line-through"
                  >
                    {{ formatTotalSelectedPackagePrice }}
                  </span>
                  {{
                    formatNumberV2(
                      totalSelectedPackagePrice -
                        getCouponDiscount(totalSelectedPackagePrice, snapshot.context.data.coupon?.duration === "ONCE"),
                      {
                        style: "currency",
                        currency: currency,
                      }
                    )
                  }}

                  <EdroneBadge
                    v-if="!isBrazil"
                    :label="`+ ${translations.drawer.options.vat.toUpperCase()}`"
                    class="tw:font-normal"
                    size="small"
                  />
                </p>
              </div>
              <p class="tw:body-2 tw:text-default-700">
                {{ translations.drawer.summary.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <EdroneNavigationBottom>
        <template #start>
          <EdroneButton v-if="showBack" type="text" @click="$emit('close')">
            <template #left-icon>
              <FontAwesomeIcon icon="fa-light fa-arrow-left" />
            </template>
            {{ translations.drawer.buttons.back }}
          </EdroneButton>
          <EdroneButton v-else type="text" @click="$emit('close')">
            {{ translations.drawer.buttons.close }}
          </EdroneButton>
        </template>
        <template #end>
          <EdroneButton type="solid" @click="send({ type: 'CONFIRM_UPGRADE' })">
            {{ translations.drawer.buttons.confirm }}
          </EdroneButton>
        </template>
      </EdroneNavigationBottom>

      <PackageManagerPricingModal
        :packages="snapshot.context.data.packages"
        :recommended-price="snapshot.context.data.recommendedPackage?.price"
        :show="snapshot.hasTag('showingPricing')"
        @close="send({ type: 'CANCEL' })"
      />
      <IntegrationModal
        :open="snapshot.hasTag('integrationRequired')"
        :desc="trans._whatsApp.readyFlows.noIntegrationModal.desc"
        :title="trans._whatsApp.readyFlows.noIntegrationModal.title"
        :url="snapshot.context.integrationUrl"
        @close="send({ type: 'CANCEL' })"
      />
      <PackageManagerConfirmModal
        :show="snapshot.hasTag('confirmingPackage') || snapshot.hasTag('updatingPackage')"
        :translations="translations"
        :subscription="snapshot.context.data.subscription"
        :packages="snapshot.context.data.packages"
        :currency="currency"
        :price="snapshot.context.data.selectedPackage?.price || 0"
        :coupon="snapshot.context.data.coupon"
        :new-package-size="snapshot.context.data.customPackageSize"
        @close="send({ type: 'CANCEL' })"
        @confirm="send({ type: 'SAVE_PACKAGE' })"
      />
      <PackageManagerAssasRequestedModal :show="snapshot.hasTag('assasRequested')" @close="send({ type: 'CANCEL' })" />
    </div>
  </EdroneDrawer>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import EdroneNavigationTop from "edrone-components/EdroneNavigationTop.vue";
import EdroneNavigationBottom from "edrone-components/EdroneNavigationBottom.vue";
import EdroneHeader from "edrone-components/EdroneHeader.vue";
import EdroneResultState from "edrone-components/EdroneResultState.vue";
import EdroneAlert from "edrone-components/EdroneAlert.vue";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";
import EdroneSliderInput from "edrone-components/EdroneSliderInput.vue";
import EdroneNumberInput from "edrone-components/EdroneNumberInput.vue";
import EdroneRadioButton from "edrone-components/EdroneRadioButton.vue";
import EdroneButton from "edrone-components/EdroneButton.vue";
import { formatNumberV2 } from "vue-app/utils/functions.ts";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import EdroneDrawer from "edrone-components/EdroneDrawer.vue";
import PackageManagerConfirmModal from "vue-app/components/PackageManager/PackageManagerConfirmModal.vue";
import PackageManagerPricingModal from "vue-app/components/PackageManager/PackageManagerPricingModal.vue";
import PackageManagerAssasRequestedModal from "vue-app/components/PackageManager/PackageManagerAssasRequestedModal.vue";
import { usePackageManagerMachine } from "vue-app/components/PackageManager/PackageManager.machine.ts";
import { CHANNEL_TYPE, COUNTRY_ID } from "vue-app/types/enums.ts";
import EdroneInput from "edrone-components/EdroneInput.vue";
import { Translations } from "vue-app/types/translations";
import IntegrationModal from "vue-app/components/common/IntegrationModal.vue";

interface Props {
  channelTypeId: CHANNEL_TYPE;
  showBack?: boolean;
  isOpen?: boolean;
  translations: Translations["_packageManager"]["sms" | "whatsapp"];
}
const props = withDefaults(defineProps<Props>(), {
  showBack: false,
  isOpen: false,
});
defineEmits(["close"]);
const { snapshot, send } = usePackageManagerMachine(props.channelTypeId);

const isBrazil = window.EDRONE_CONTEXT.tracker.countryId === COUNTRY_ID.BRAZIL;
const currency = computed(() => snapshot.value.context.data.packages?.currency?.toUpperCase() || "PLN");

const isValidatingCoupon = computed(() => snapshot.value.hasTag("validatingCoupon"));
const invalidCoupon = computed(() => snapshot.value.context.data.coupon && !snapshot.value.context.data.coupon.active);

const selected = ref(0);
const showCoupon = ref(true);

watch(
  () => snapshot.value.context.data.recommendedPackage,
  (value) => {
    if (!value) {
      selected.value = 1;
    }
  },
  {
    immediate: true,
  }
);

const totalSelectedPackagePrice = computed(
  () => (snapshot.value.context.data.customPackageSize || 0) * snapshot.value.context.data.selectedPackage.price
);
const formatTotalSelectedPackagePrice = computed(() =>
  formatNumberV2(totalSelectedPackagePrice.value, {
    style: "currency",
    currency: currency.value,
  })
);

const recommendedDiscount = computed(() => {
  if (snapshot.value.context.data.packages?.tierPrices && snapshot.value.context.data.packages.tierPrices[0]) {
    return snapshot.value.context.data.recommendedPackage
      ? Math.floor(
          100 -
            (snapshot.value.context.data.recommendedPackage.price /
              snapshot.value.context.data.packages.tierPrices[0].price) *
              100
        )
      : 0;
  }
  return 0;
});

function handleSelectRecommended() {
  selected.value = 0;
  send({ type: "CHANGE_PACKAGE_SIZE", size: snapshot.value.context.data.recommendedPackageSize });
}

function handleSelectCustom() {
  selected.value = 1;
  send({ type: "CHANGE_PACKAGE_SIZE", size: snapshot.value.context.data.customPackageSize });
}

function handlePackageSizeChange(value: number) {
  const maxSize = 100001;
  if (value >= 1 && value <= maxSize) {
    send({ type: "CHANGE_PACKAGE_SIZE", size: value });
    return;
  }
  send({ type: "CHANGE_PACKAGE_SIZE", size: value < 1 ? 1 : maxSize });
}

function getCouponInfo(): string {
  if (!snapshot.value.context.data.coupon || !snapshot.value.context.data.coupon.active) {
    return props.translations.drawer.coupon.invalid;
  }
  const value = snapshot.value.context.data.coupon.amountOff || snapshot.value.context.data.coupon.percentOff;
  const unit = snapshot.value.context.data.coupon.amountOff ? ` ${currency.value}` : "%";
  const description =
    snapshot.value.context.data.coupon.duration === "ONCE"
      ? props.translations.drawer.coupon.success.once
      : props.translations.drawer.coupon.success.forever;
  return `${snapshot.value.context.data.coupon.promotionCode} ${props.translations.drawer.coupon.success.applied} (${value}${unit} ${description})`;
}

function getCouponDiscount(price: number, once?: boolean): number {
  if (!snapshot.value.context.data.coupon?.active || once) {
    return 0;
  }
  const discount =
    snapshot.value.context.data.coupon.amountOff ||
    (snapshot.value.context.data.coupon.percentOff ? (price * snapshot.value.context.data.coupon.percentOff) / 100 : 0);
  return discount > price ? price : discount;
}
</script>
